import { computed, inject, Injectable, Injector, runInInjectionContext, Signal } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { activate, fetchAndActivate, getRemoteConfig, getValue, onConfigUpdate, RemoteConfig } from '@angular/fire/remote-config';
import { MatDialog } from '@angular/material/dialog';
import { RemoteConfigState } from '@app/core/firebase/remote-config/remote-config.state';
import { RemoteConfigDefaults, RemoteConfigKeys } from '@app/core/firebase/remote-config/remote-config.types';
import { environment } from '@environments/environment';
import { MaintenanceDialogComponent } from '@shared/components/maintenance/maintenance-dialog.component';
import { isEmpty } from 'lodash';

@Injectable({ providedIn: 'root' })
export class RemoteConfigService {
  private _remoteConfig: RemoteConfig = inject(RemoteConfig);
  private _remoteConfigState: RemoteConfigState = inject(RemoteConfigState);
  private _injector: Injector = inject(Injector);
  private _app: FirebaseApp = inject(FirebaseApp);
  private _dialog: MatDialog = inject(MatDialog);
  private _config: Signal<RemoteConfigKeys> = computed(() => this._remoteConfigState.remoteConfig());

  constructor() {
    this._remoteConfig = getRemoteConfig(this._app);
    this.initializeFirebaseRemoteConfig();

    /**
     * minimumFetchIntervalMillis is how often we can fetchAndActivate() successfully (each user has their individual cooldown time)
     * This cooldown does not apply to onConfigUpdate which is triggered by real-time updates to our firebase remote config
     */
    const ONE_HOUR = 60 * 60 * 1000;
    this._remoteConfig.settings.minimumFetchIntervalMillis = 12 * ONE_HOUR;
  }

  public initializeFirebaseRemoteConfig(fetchOnly: boolean = false): void {
    if (environment.firebaseConfig.projectId?.includes('staging')) return;

    this._remoteConfig.defaultConfig = RemoteConfigDefaults;
    fetchAndActivate(this._remoteConfig)
      .then(() => {
        this.getRemoteConfig();
        this._showOverlay();
      })
      .catch((err) => console.error('Failed to fetch Remote Config', err));

    if (fetchOnly) return;

    onConfigUpdate(this._remoteConfig, {
      next: (configUpdate) => {
        runInInjectionContext(this._injector, () => {
          if (!isEmpty(configUpdate.getUpdatedKeys())) {
            activate(this._remoteConfig).then(() => {
              this.getRemoteConfig();
              this._showOverlay();
            });
          }
        });
      },
      error: (error) => {
        console.log('Config update error:', error);
      },
      complete: () => {
        console.log('Listening stopped.');
      }
    });
  }

  public getRemoteConfig(): void {
    runInInjectionContext(this._injector, () => {
      const keys = Object.keys(RemoteConfigDefaults);
      const newConfig = {} as RemoteConfigKeys;

      keys.forEach((key) => {
        const value = getValue(this._remoteConfig, key);
        const defaultValue = RemoteConfigDefaults[key];

        if (typeof defaultValue === 'boolean') {
          newConfig[key] = value.asBoolean();
        } else if (typeof defaultValue === 'number') {
          newConfig[key] = value.asNumber();
        } else if (typeof defaultValue === 'object') {
          newConfig[key] = JSON.parse(value.asString());
        } else {
          newConfig[key] = value.asString();
        }
      });

      this._remoteConfigState.set({ remoteConfig: newConfig });
    });
  }

  public refreshFirebaseRemoteConfig(): void {
    if (this._config()) return;
    this.initializeFirebaseRemoteConfig(true);
  }

  private _showOverlay(): void {
    const isMaintenanceMode = this._config().maintenance_mode;
    const dialogRef = this._dialog.getDialogById('maintenance-dialog');

    if (isMaintenanceMode && !dialogRef) {
      this._dialog.open(MaintenanceDialogComponent, {
        id: 'maintenance-dialog',
        panelClass: 'full-screen-dialog',
        disableClose: true,
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh'
      });
    } else if (!isMaintenanceMode && dialogRef) {
      dialogRef.close();
    }
  }
}
